// src/components/company/CompanyLayout.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompanySidebar from "./CompanySidebar";
import { Menu, X, Building2 } from "lucide-react";
import OrgSwitcher from "./OrgSwitcher";
import AppNavbar from "../AppNarbar";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { useBranding } from "../../contexts/BrandingContext";
import useDialog from "@/hooks/useDialog";

export default function CompanyLayout({ org, header, children }) {
  const [open, setOpen] = useState(false);
  const branding = useBranding();
  const navigate = useNavigate();
  const pageBg = branding.branded ? branding.accent_color : "var(--bg)";
  const drawerBg = branding.branded ? branding.accent_color : "#FCFAF5";

  // The drawer covers the page with a scrim, so it is modal for the keyboard
  // too: focus stays in it until it closes. It only exists below lg, so close
  // it if the window grows past that rather than trap focus in a hidden panel.
  const drawer = useDialog({ open, onClose: () => setOpen(false) });
  useEffect(() => {
    if (!open) return undefined;
    const wide = window.matchMedia("(min-width: 1024px)");
    const onChange = () => wide.matches && setOpen(false);
    wide.addEventListener("change", onChange);
    return () => wide.removeEventListener("change", onChange);
  }, [open]);

  // ✅ centralize org context here so pages don't have to
  const [resolvedOrg, setResolvedOrg] = useState(org || null);
  const [orgLoading, setOrgLoading] = useState(!org);

  // Time-entry-only roles should never see the company sidebar/dashboard.
  // Redirect them to the dedicated /clock page if they end up here.
  useEffect(() => {
    const role = resolvedOrg?.role;
    if (role === "Time Entry - Clock" || role === "Time Entry - Manual") {
      navigate("/clock", { replace: true });
    }
  }, [resolvedOrg, navigate]);

  useEffect(() => {
    let mounted = true;

    // If page passed org, trust it.
    if (org) {
      setResolvedOrg(org);
      setOrgLoading(false);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      setOrgLoading(true);
      try {
        const me = await fetchWithAuth("/api/accounts/me/");

        // Prefer the new shape
        const orgFromMe = me?.organization || me?.org || null;

        // Fallback for older shape
        const orgFromMembership =
          me?.memberships?.[0]?.organization ||
          me?.organization_memberships?.[0]?.organization ||
          null;

        if (mounted) {
          setResolvedOrg(orgFromMe || orgFromMembership || null);
        }
      } catch (e) {
        console.error("CompanyLayout: failed to load org context:", e);
        if (mounted) setResolvedOrg(null);
      } finally {
        if (mounted) setOrgLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [org]);

  const displayOrgName = useMemo(() => {
    if (resolvedOrg?.name) return resolvedOrg.name;
    if (orgLoading) return "Loading…";
    return "Company";
  }, [resolvedOrg, orgLoading]);

  // Reload after switching org so all org-scoped data refetches
  const handleOrgChange = () => {
    try {
      window.location.reload();
    } catch (_) {}
  };

  return (
    <div className="min-h-screen text-ink" style={{ backgroundColor: pageBg }}>
      <AppNavbar />

      {/* Mobile company bar (secondary to the global navbar) */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-line bg-surface">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-tertiary" />
          <div className="text-sm text-secondary min-w-0 break-words">{displayOrgName}</div>
        </div>
        <button
          className="p-2 rounded-lg hover:bg-sunken text-ink"
          onClick={() => setOpen(true)}
          aria-label="Open company menu"
        >
          <Menu />
        </button>
      </div>

      {/* Mobile drawer for company nav. z-[60] so it sits above the sticky
          AppNavbar (z-50); otherwise the navbar covers the top of the drawer. */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div role="presentation" className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <div {...drawer.props} aria-labelledby={undefined} aria-label="Company menu" className="absolute left-0 top-0 h-full w-72">
            <div className="h-full flex flex-col pt-[env(safe-area-inset-top)]" style={{ backgroundColor: drawerBg }}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-line">
                <div className="font-semibold min-w-0 break-words text-ink">{displayOrgName}</div>
                <button aria-label="Close menu" className="p-2 rounded-lg hover:bg-sunken text-ink" onClick={() => setOpen(false)}>
                  <X />
                </button>
              </div>

              {/* Org switcher */}
              <div className="px-4 py-3 border-b border-line">
                <OrgSwitcher onChanged={handleOrgChange} />
              </div>

              {/* Company sidebar nav */}
              <div className="flex-1 overflow-auto">
                <CompanySidebar org={resolvedOrg} onNavigate={() => setOpen(false)} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop shell: fixed sidebar + fluid main (min-w-0 prevents wide
          content from clipping under the rail). */}
      <div className="max-w-[1480px] mx-auto flex gap-8 px-4 sm:px-6 lg:px-10 py-6">
        <aside className="hidden lg:block w-[248px] shrink-0 sticky top-24 self-start">
          <CompanySidebar org={resolvedOrg} />
        </aside>
        <main className="flex-1 min-w-0 space-y-6 pb-[env(safe-area-inset-bottom)]">
          {header}
          {children}
        </main>
      </div>
    </div>
  );
}
